import apiFetch from "@/app/auth/apiFetch";
import type { NavigateFunction } from "react-router-dom";

const API_URL = import.meta.env.VITE_API_URL;

export interface ClassificationItem {
  id: number;
  campaign_candidate_id: number;
  candidate_id: number | null;
  candidate_name: string | null;
  predicted_label: string;
  confidence: number;
  engine: string;
  model_version: string;
  ground_truth_label: string | null;
  created_at: string;
  message: string | null;
}

export interface PaginatedClassificationsResponse {
  items: ClassificationItem[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface ClassificationStats {
  total: number;
  annotated: number;
  remaining: number;
  by_predicted_label: Record<string, number>;
  by_ground_truth_label: Record<string, number>;
  training_examples_total: number;
}

export interface TrainingExampleItem {
  id: number;
  text: string;
  label: string;
  source: string;
  created_by: string | null;
  created_at: string;
}

export interface PaginatedTrainingExamplesResponse {
  items: TrainingExampleItem[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export async function listClassifications(
  navigate: NavigateFunction,
  params?: { page?: number; size?: number; annotated?: boolean },
): Promise<PaginatedClassificationsResponse> {
  const q = new URLSearchParams();
  q.set("page", String(params?.page ?? 1));
  q.set("size", String(params?.size ?? 25));
  if (params?.annotated !== undefined) q.set("annotated", String(params.annotated));
  const res = await apiFetch(`${API_URL}/admin/classifications?${q.toString()}`, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du chargement des classifications");
  return res.json();
}

export async function getClassificationStats(navigate: NavigateFunction): Promise<ClassificationStats> {
  const res = await apiFetch(`${API_URL}/admin/classifications/stats`, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du chargement des statistiques");
  return res.json();
}

export async function annotateClassification(
  navigate: NavigateFunction,
  id: number,
  label: string,
): Promise<{ id: number; ground_truth_label: string; training_example_created: boolean }> {
  const res = await apiFetch(
    `${API_URL}/admin/classifications/${id}/annotate`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ label }),
    },
    navigate,
  );
  if (!res?.ok) throw new Error("Erreur lors de l'annotation");
  return res.json();
}

export async function listTrainingExamples(
  navigate: NavigateFunction,
  params?: { page?: number; size?: number },
): Promise<PaginatedTrainingExamplesResponse> {
  const q = new URLSearchParams();
  q.set("page", String(params?.page ?? 1));
  q.set("size", String(params?.size ?? 25));
  const res = await apiFetch(`${API_URL}/admin/classifications/training-examples?${q.toString()}`, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du chargement des exemples");
  return res.json();
}

export async function createTrainingExample(
  navigate: NavigateFunction,
  payload: { text: string; label: string },
): Promise<TrainingExampleItem> {
  const res = await apiFetch(
    `${API_URL}/admin/classifications/training-examples`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
    navigate,
  );
  if (!res?.ok) throw new Error("Erreur lors de l'ajout de l'exemple");
  return res.json();
}

export async function deleteTrainingExample(navigate: NavigateFunction, id: number): Promise<void> {
  const res = await apiFetch(`${API_URL}/admin/classifications/training-examples/${id}`, { method: "DELETE" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors de la suppression de l'exemple");
}

// --- Seuils de confiance (gate REVIEW → TODO côté back) ---

export interface ThresholdConfig {
  global_threshold: number;
  overrides: Record<string, number>;
  defaults: { global: number; per_class: Record<string, number> };
  labels: string[];
  gate_enabled: boolean;
  updated: Record<string, { updated_at: string | null; updated_by: string | null }>;
}

export interface ThresholdEntry {
  scope: string;
  // null sur un scope classe = suppression de l'override (retour au global).
  threshold: number | null;
}

export interface LowConfidenceStats {
  days: number;
  stats: Record<
    string,
    { total: number; low_confidence: number; rate: number; avg_confidence: number | null }
  >;
}

export async function getThresholdConfig(navigate: NavigateFunction): Promise<ThresholdConfig> {
  const res = await apiFetch(`${API_URL}/admin/classifications/threshold-config`, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du chargement des seuils de confiance");
  return res.json();
}

export async function updateThresholdConfig(
  navigate: NavigateFunction,
  entries: ThresholdEntry[],
): Promise<{ status: string; upserted: string[]; deleted: string[] }> {
  const res = await apiFetch(
    `${API_URL}/admin/classifications/threshold-config`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ entries }),
    },
    navigate,
  );
  if (!res?.ok) {
    let detail = "Erreur lors de la mise à jour des seuils";
    try {
      const body = await res?.json();
      if (body?.detail) detail = typeof body.detail === "string" ? body.detail : detail;
    } catch {
      // corps non JSON — message générique
    }
    throw new Error(detail);
  }
  return res.json();
}

export async function getLowConfidenceStats(navigate: NavigateFunction, days = 7): Promise<LowConfidenceStats> {
  const res = await apiFetch(
    `${API_URL}/admin/classifications/threshold-config/low-confidence-stats?days=${days}`,
    { method: "GET" },
    navigate,
  );
  if (!res?.ok) throw new Error("Erreur lors du chargement des statistiques basse confiance");
  return res.json();
}
